import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { Trophy, Calendar, ChevronRight, ArrowLeft, Shield } from "lucide-react";
import { API_URL as API } from "../config";

const statusColor = (status) => {
  switch (status) {
    case "completed": return "bg-emerald-100 text-emerald-700";
    case "in_progress": case "active": return "bg-amber-100 text-amber-700";
    default: return "bg-blue-100 text-blue-700";
  }
};

function TournamentList({ tournaments, onSelect, bt }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {tournaments.map(t => (
        <button key={t.id} onClick={() => onSelect(t.id)} data-testid={`tournament-card-${t.id}`}
          className="bg-white rounded-2xl border border-gray-100 p-6 text-left hover:shadow-lg hover:border-[#87CEEB]/40 transition-all group">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 bg-[#87CEEB]/10 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-[#87CEEB]" />
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColor(t.status)}`}>
              {t.status === "completed" ? bt.completed : t.status === "in_progress" || t.status === "active" ? bt.active : bt.upcoming}
            </span>
          </div>
          <h3 className="text-lg font-bold text-gray-900 font-['Kanit'] mb-2 group-hover:text-[#87CEEB] transition-colors">{t.name}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <Calendar className="w-4 h-4" />
            <span>{t.start_date}{t.end_date ? ` — ${t.end_date}` : ""}</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-400">
            <Shield className="w-3.5 h-3.5" />
            <span>{(t.team_ids || []).length} {bt.team.toLowerCase()}s</span>
          </div>
          <div className="mt-4 flex items-center gap-1 text-sm text-[#87CEEB] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            {bt.viewBracket} <ChevronRight className="w-4 h-4" />
          </div>
        </button>
      ))}
    </div>
  );
}

function TournamentDetail({ tournamentId, onBack, bt }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("standings");

  useEffect(() => {
    fetch(`${API}/api/tournaments/public/${tournamentId}`)
      .then(r => r.json()).then(setData).catch(() => {});
  }, [tournamentId]);

  if (!data) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin w-8 h-8 border-4 border-[#87CEEB] border-t-transparent rounded-full" />
    </div>
  );

  const { tournament, matches, standings } = data;

  return (
    <div>
      <button onClick={onBack} data-testid="back-to-tournaments"
        className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 text-sm font-medium transition-colors">
        <ArrowLeft className="w-4 h-4" /> {bt.allTournaments}
      </button>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#87CEEB]/10 rounded-xl flex items-center justify-center">
              <Trophy className="w-7 h-7 text-[#87CEEB]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{tournament.name}</h2>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Calendar className="w-4 h-4" />
                <span>{tournament.start_date}{tournament.end_date ? ` — ${tournament.end_date}` : ""}</span>
              </div>
            </div>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${statusColor(tournament.status)}`}>
            {tournament.status === "completed" ? bt.completed : tournament.status === "in_progress" || tournament.status === "active" ? bt.active : bt.upcoming}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {["standings", "matches"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
              tab === t ? "bg-[#87CEEB] text-white shadow-md" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
            }`}>
            {t === "standings" ? bt.standings : bt.matches}
          </button>
        ))}
      </div>

      {/* Standings Table */}
      {tab === "standings" && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="standings-table">
          {standings.length === 0 ? (
            <p className="text-gray-400 text-sm py-12 text-center">{bt.noMatches}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left">#</th>
                    <th className="py-3 px-4 text-left">{bt.team}</th>
                    <th className="py-3 px-3 text-center">{bt.played}</th>
                    <th className="py-3 px-3 text-center">{bt.won}</th>
                    <th className="py-3 px-3 text-center">{bt.drawn}</th>
                    <th className="py-3 px-3 text-center">{bt.lost}</th>
                    <th className="py-3 px-3 text-center">{bt.goalsFor}</th>
                    <th className="py-3 px-3 text-center">{bt.goalsAgainst}</th>
                    <th className="py-3 px-3 text-center">{bt.goalDiff}</th>
                    <th className="py-3 px-3 text-center font-bold">{bt.pts}</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((s, i) => (
                    <tr key={s.team_id} className={`border-t border-gray-50 ${i === 0 ? 'bg-amber-50/30' : ''}`}>
                      <td className="py-3 px-4 font-bold text-gray-400">{i + 1}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || '#87CEEB' }} />
                          <span className="font-medium text-gray-800">{s.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center text-gray-600">{s.played}</td>
                      <td className="py-3 px-3 text-center text-emerald-600 font-medium">{s.wins}</td>
                      <td className="py-3 px-3 text-center text-gray-500">{s.draws}</td>
                      <td className="py-3 px-3 text-center text-red-500">{s.losses}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{s.goals_for}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{s.goals_against}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{s.goals_for - s.goals_against}</td>
                      <td className="py-3 px-3 text-center font-bold text-gray-900">{s.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Matches */}
      {tab === "matches" && (
        <div className="space-y-3" data-testid="matches-list">
          {matches.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <p className="text-gray-400 text-sm">{bt.noMatches}</p>
            </div>
          ) : (
            matches.map(m => (
              <div key={m.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-sm transition-shadow" data-testid={`match-card-${m.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{m.date}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(m.status)}`}>
                    {m.status === "completed" ? bt.completed : m.status === "in_progress" ? bt.inProgress : bt.scheduled}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.team1_color || '#87CEEB' }} />
                    <span className="font-medium text-gray-800 text-sm">{m.team1_name}</span>
                  </div>
                  {m.status === "completed" ? (
                    <div className="flex items-center gap-3 px-4">
                      <span className={`text-xl font-bold font-['Kanit'] ${(m.score1 || 0) > (m.score2 || 0) ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {m.score1 ?? 0}
                      </span>
                      <span className="text-gray-300 text-xs">-</span>
                      <span className={`text-xl font-bold font-['Kanit'] ${(m.score2 || 0) > (m.score1 || 0) ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {m.score2 ?? 0}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-300 text-sm px-4 font-medium">{bt.vs}</span>
                  )}
                  <div className="flex items-center gap-2 flex-1 justify-end">
                    <span className="font-medium text-gray-800 text-sm">{m.team2_name}</span>
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.team2_color || '#87CEEB' }} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function TournamentBracketsPage() {
  const { t } = useLanguage();
  const bt = t.brackets || {};
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/tournaments/public`)
      .then(r => r.json())
      .then(data => { setTournaments(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Tournament Brackets - Curious 123</title>
        <meta name="description" content="Follow Curious 123 youth soccer tournaments, standings and match results" />
      </Helmet>

      <section className="pt-28 pb-20 min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="tournament-brackets-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {!selectedId ? (
            <>
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#87CEEB]/10 text-[#87CEEB] rounded-full text-sm font-medium mb-4">
                  <Trophy className="w-4 h-4" /> {bt.title || "Tournament Brackets"}
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 font-['Kanit'] mb-4">
                  {bt.title || "Tournament Brackets"}
                </h1>
                <p className="text-gray-500 max-w-xl mx-auto">
                  {bt.subtitle || "Follow our youth soccer tournaments, standings, and match results"}
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin w-8 h-8 border-4 border-[#87CEEB] border-t-transparent rounded-full" />
                </div>
              ) : tournaments.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                  <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-400">{bt.noTournaments || "No tournaments scheduled yet. Check back soon!"}</p>
                </div>
              ) : (
                <TournamentList tournaments={tournaments} onSelect={setSelectedId} bt={bt} />
              )}
            </>
          ) : (
            <TournamentDetail tournamentId={selectedId} onBack={() => setSelectedId(null)} bt={bt} />
          )}
        </div>
      </section>
    </>
  );
}
